import axios from 'axios';
import Cookies from 'universal-cookie';
import { jwtDecode } from 'jwt-decode';

const cookies = new Cookies();

/**
 * Fetches all apiaries.
 * @returns {Promise<Array>} - List of all apiaries.
 */
export const getAllApiariesService = async (projectId = null) => {
    try {
        const token = cookies.get('access_token');
        let url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/apiaries`;
        if (projectId && projectId !== 'ALL') {
            url += `?project_id=${projectId}`;
        }

        const response = await axios.get(url, {
            headers: {
                'Content-Type': 'application/json',
                'source': 'humblebee_admin',
                'Authorization': `Bearer ${token}`,
            }
        });

        const raw = response.data;
        if (Array.isArray(raw)) return raw;
        if (Array.isArray(raw?.data)) return raw.data;
        if (Array.isArray(raw?.apiaries)) return raw.apiaries;
        return [];
    } catch (error) {
        console.error('Error in getAllApiariesService:', error.response || error);
        return [];
    }
};

export const getApiaryDetails = async (apiaryId) => {
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/apiaries/${apiaryId}`;

        const response = await axios.get(url, {
            headers: {
                'Content-Type': 'application/json',
                'source': 'humblebee_admin',
                'Authorization': `Bearer ${token}`,
            }
        });

        return response.data || null;
    } catch (error) {
        console.error('Error in getApiaryDetails:', error.response || error);
        throw error;
    }
};

export const getObservations = async (apiaryId) => {
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/observations?apiary_id=${apiaryId}`;

        const response = await axios.get(url, {
            headers: {
                source: "humblebee_admin",
                Authorization: "Bearer " + token,
            }
        });
        return response.data.observations || [];
    } catch (error) {
        console.error('Error in getObservations:', error);
        return [];
    }
};

/**
 * Fetches all observations across all apiaries, optionally filtered by date range or observer.
 * @param {Object} params - Query parameters (date_from, date_to, observed_by, apiary_id).
 * @returns {Promise<Array>} - List of observation records.
 */
export const getAllObservations = async (params = {}) => {
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/observations`;

        const response = await axios.get(url, {
            params,
            headers: {
                source: "humblebee_admin",
                Authorization: "Bearer " + token,
            }
        });
        return response.data?.observations || [];
    } catch (error) {
        console.error('Error in getAllObservations:', error);
        return [];
    }
};
export const addObservation = async (data) => {
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/observations`;
        const response = await axios.post(url, data, {
            headers: {
                source: "humblebee_admin",
                Authorization: "Bearer " + token,
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error in addObservation:', error.response || error);
        throw error;
    }
}

export const updateObservation = async (id, data) => {
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/observations/${id}`;
        const response = await axios.patch(url, data, {
            headers: {
                source: "humblebee_admin",
                Authorization: "Bearer " + token,
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error in updateObservation:', error.response || error);
        throw error;
    }
}

/**
 * Uploads photos for a newly created observation.
 * @param {string} observationId - The ID of the created observation (observation_id).
 * @param {FormData} formData - The formData containing `photos` and `photos_metadata`.
 * @returns {Promise<Object>} - The result of the upload.
 */
export const uploadObservationPhotos = async (observationId, formData) => {
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/observations/${observationId}/photos`;
        const response = await axios.post(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                source: "humblebee_admin",
                Authorization: "Bearer " + token
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error uploading observation photos:", error.response || error);
        throw error;
    }
};

export const getExpenses = async (apiaryId, filters = {}) => {
    try {
        const token = cookies.get('access_token');
        let url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/expenses?apiary_id=${apiaryId}`;

        if (filters.category && filters.category !== 'all') {
            url += `&category=${filters.category.toUpperCase()}`;
        }
        if (filters.date_from) {
            url += `&date_from=${filters.date_from}`;
        }
        if (filters.date_to) {
            url += `&date_to=${filters.date_to}`;
        }
        if (filters.paid_by && filters.paid_by !== 'all') {
            url += `&paid_by=${filters.paid_by}`;
        }

        const response = await axios.get(url, {
            headers: {
                'Content-Type': 'application/json',
                source: "humblebee_admin",
                Authorization: "Bearer " + token
            }
        });

        return response.data;
    } catch (error) {
        console.error("Error fetching financial logs:", error);
        return { expenses: [] };
    }
}

/**
 * Fetches user details by user ID from the user-app API.
 * @param {string} userId - The unique user ID to fetch
 * @returns {Promise<Object>} - The user details object or null
 */
export const getUserDetails = async (userId) => {
    if (!userId || typeof userId !== 'string') return null;
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_API_APP_USER_URL}/user/user-id-details/${userId}`;
        const response = await axios.get(url, {
            headers: {
                source: "humblebee_admin",
                Authorization: "Bearer " + token
            }
        });
        return response.data?.user || null;
    } catch (error) {
        console.error(`Error fetching user details for ${userId}:`, error.response?.data || error.message);
        return null;
    }
};

/**
 * Creates a new expense record for an apiary.
 * @param {Object} data - The expense data payload.
 * @returns {Promise<Object>} - The created expense object.
 */
export const addExpense = async (data) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL;
        // Admin web route: reads paid_by from the body (the /app route now takes the user from the token/header).
        const url = `${fieldApi}/nector/web/expenses`;
        const response = await axios.post(url, data, {
            headers: {
                'Content-Type': 'application/json',
                source: "humblebee_admin",
                Authorization: "Bearer " + token
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error adding expense:", error.response || error);
        throw error;
    }
};

/**
 * Creates a Non-Apiary expense on behalf of a specific team member (Admin -> + Add Entry).
 * Hits the same POST /wallet/non-apiary-expense endpoint the mobile app uses; that route's
 * create_non_apiary_expense controller already accepts an explicit user_id in the body
 * (no backend change was needed for this one, unlike the Apiary path). It's a
 * multipart/form-data endpoint (bill-upload support), so this sends FormData with no
 * file attached rather than JSON.
 */
export const addNonApiaryExpense = async (data) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL;
        const url = `${fieldApi}/wallet/non-apiary-expense`;

        const formData = new FormData();
        formData.append('user_id', data.user_id);
        formData.append('expense_date', data.expense_date);
        formData.append('category', data.category || 'MISC');
        formData.append('amount', String(data.amount));
        formData.append('expensed_by', data.expensed_by || '');
        formData.append('details', data.details || '');
        if (data.reporting_to) {
            formData.append('reporting_to', String(data.reporting_to));
        }

        const response = await axios.post(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                source: "humblebee_admin",
                Authorization: "Bearer " + token
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error adding non-apiary expense:", error.response || error);
        throw error;
    }
};

/**
 * REAL SERVICE LAYER FOR ADVANCES (Backend is now ready)
 */

/**
 * Fetches all advances for an apiary.
 */
export const getAdvances = async (apiary_id) => {
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/advances${apiary_id ? `?apiary_id=${apiary_id}` : ''}`;
        const response = await axios.get(url, {
            headers: { source: "humblebee_admin", Authorization: "Bearer " + token }
        });
        return response.data.data || [];
    } catch (error) {
        console.error("Error fetching advances:", error);
        return [];
    }
};

/**
 * Fetches all advances.
 */
export const getAllAdvances = async () => {
    return getAdvances(null);
};

/**
 * Fetches personnel/supervisors for the dropdown.
 */
export const getSupervisors = async () => {
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/supervisors`;
        const response = await axios.get(url, {
            headers: { source: "humblebee_admin", Authorization: "Bearer " + token }
        });
        return response.data?.supervisors || response.data?.data || (Array.isArray(response.data) ? response.data : []);
    } catch (error) {
        console.error("Error fetching supervisors:", error);
        return [];
    }
};

/**
 * Fetches all Humblebee projects for Project P&L and filters.
 */
export const getAllProjectsService = async () => {
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_HUMBLEBEE_API_URL || process.env.REACT_APP_API_URL}/projects/`;
        const response = await axios.get(url, {
            headers: { authorization: `Bearer ${token}` }
        });
        return response.data?.projects || response.data?.data || response.data || [];
    } catch (error) {
        console.warn("Could not fetch projects from /projects/, falling back to empty:", error);
        return [];
    }
};

/**
 * Creates a new advance.
 */
export const addAdvance = async (data) => {
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/advances`;
        const response = await axios.post(url, data, {
            headers: { source: "humblebee_admin", Authorization: "Bearer " + token }
        });
        return response.data.data;
    } catch (error) {
        console.error("Error adding advance:", error);
        throw error;
    }
};

/**
 * Deletes an advance.
 */
export const deleteAdvance = async (advanceId) => {
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/advances/${advanceId}`;
        const response = await axios.delete(url, {
            headers: { source: "humblebee_admin", Authorization: "Bearer " + token }
        });
        return response.data;
    } catch (error) {
        console.error("Error deleting advance:", error.response || error);
        throw error;
    }
};


/**
 * Allocates an expense to an advance.
 */
export const allocateExpenseToAdvance = async (expenseId, advanceId, amount) => {
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/advances/allocate`;
        const response = await axios.post(url, { expense_id: expenseId, advance_id: advanceId, amount }, {
            headers: { source: "humblebee_admin", Authorization: "Bearer " + token }
        });
        return response.data;
    } catch (error) {
        console.error("Error allocating expense:", error);
        throw error;
    }
};

/**
 * Enhanced getExpenses to include real allocation data.
 */
export const getExpensesWithAllocation = async (apiaryId, filters = {}) => {
    try {
        const token = cookies.get('access_token');
        let url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/expenses?apiary_id=${apiaryId}&include_settlement=true`;

        if (filters.category && filters.category !== 'all') url += `&category=${filters.category.toUpperCase()}`;
        if (filters.date_from) url += `&date_from=${filters.date_from}`;
        if (filters.date_to) url += `&date_to=${filters.date_to}`;
        if (filters.paid_by && filters.paid_by !== 'all') url += `&paid_by=${filters.paid_by}`;

        const response = await axios.get(url, {
            headers: { source: "humblebee_admin", Authorization: "Bearer " + token }
        });

        const res = response.data;
        if (res.expenses) {
            res.expenses = res.expenses.map(exp => {
                if (exp.settlement) {
                    return {
                        ...exp,
                        allocated_to_advance: {
                            _id: exp.settlement.advance_id,
                            // Note: ID display might need a separate lookup or backend should include it
                            advance_id_display: exp.settlement.advance_id_display || 'Linked' 
                        }
                    };
                }
                return exp;
            });
        }
        return res;
    } catch (error) {
        console.error("Error fetching financial logs:", error);
        return { expenses: [] };
    }
};

/**
 * Fetches all expenses globally (combines humblebee_expenses AND field_view_non_apiary_expenses).
 */
export const getAllExpensesGlobal = async () => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://api.udyamita.org/field-view";
        const headers = { source: "humblebee_admin", Authorization: "Bearer " + token };

        // 1. Fetch Apiary Expenses from /nector/app/expenses
        const apiaryExpensesPromise = axios.get(`${fieldApi}/nector/app/expenses`, { headers })
            .then(res => res.data?.expenses || res.data || [])
            .catch(err => {
                console.warn('Error fetching apiary expenses:', err);
                return [];
            });

        // 2. Fetch Non-Apiary Expenses across all personnel via /wallet/summary/:userId
        const nonApiaryExpensesPromise = (async () => {
            try {
                const supsRes = await axios.get(`${fieldApi}/nector/app/supervisors`, { headers });
                const sups = supsRes.data?.supervisors || supsRes.data?.data || (Array.isArray(supsRes.data) ? supsRes.data : []);
                const supMap = new Map();
                sups.forEach(s => {
                    const sId = String(s._id || s.id || '');
                    const empId = s.user_employee_id || s.employee_id || s.paid_by_employee_id || s.employeeId || s.employeeCode || null;
                    const name = s.name || s.user_name || s.fullName;
                    if (sId) supMap.set(sId, { name, employee_id: empId });
                    if (name) supMap.set(name.trim().toLowerCase(), { name, employee_id: empId });
                });
                const userIds = new Set(sups.map(s => String(s._id || s.id)).filter(Boolean));
                
                const walletResults = await Promise.all(
                    Array.from(userIds).map(uId => 
                        axios.get(`${fieldApi}/wallet/summary/${uId}`, { headers })
                            .then(res => {
                                const list = res.data?.transactions || res.data?.requests || res.data?.data || (Array.isArray(res.data) ? res.data : []);
                                const sup = supMap.get(String(uId));
                                return list.map(item => ({
                                    ...item,
                                    _wallet_owner_id: uId,
                                    _wallet_owner_name: sup?.name || null,
                                    _wallet_owner_emp_id: sup?.employee_id || null,
                                }));
                            })
                            .catch(() => [])
                    )
                );

                const nonApiaryList = [];
                walletResults.flat().forEach(t => {
                    const isNonApiary =
                        t.tx_type === 'NON_APIARY_EXPENSE' ||
                        t.expense_type === 'non_apiary' ||
                        t.is_apiary === false ||
                        (!t.apiary_id && !t.apiaryId && (t.tx_type === 'EXPENSE' || t.type === 'expense' || t.category));

                    if (isNonApiary) {
                        const ownerId = t.user_id || t.paid_by || t._wallet_owner_id;
                        const userSup = supMap.get(String(ownerId)) || supMap.get(String(t._wallet_owner_id));
                        
                        const rawName = t.user_name || t.expensed_by || t.paid_by_name;
                        const displayName = (rawName && rawName !== 'Supervisor') ? rawName : (userSup?.name || t._wallet_owner_name || 'Staff');
                        
                        const empId = t.user_employee_id || 
                                      t.employee_id || 
                                      t.paid_by_employee_id || 
                                      t.created_by_employee_id || 
                                      userSup?.employee_id || 
                                      t._wallet_owner_emp_id || 
                                      null;

                        nonApiaryList.push({
                            ...t,
                            user_employee_id: empId,
                            employee_id: empId,
                            paid_by_employee_id: empId,
                            created_by_employee_id: empId,
                            _id: t.id || t._id,
                            expense_id: t.id || t._id,
                            expense_date: t.date || t.expense_date || t.created_at,
                            amount: Number(t.amount) || 0,
                            category: t.category || 'MISC',
                            paid_by: ownerId,
                            user_id: ownerId,
                            payout_status: t.payout_status || 'UNPAID',
                            incurred_by_role: displayName,
                            user_name: displayName,
                            paid_by_name: displayName,
                            expensed_by: displayName,
                            project_id: t.project_id || null,
                            project_name: t.project_name || '',
                            reporting_to: t.reporting_to || null,
                            reporting_to_name: t.reporting_to_name || (typeof t.reporting_to === 'object' ? (t.reporting_to?.name || t.reporting_to?.username) : null) || null,
                            details: t.details || t.notes || t.remarks || '',
                            notes: t.details || t.remarks || t.title || '',
                            remarks: t.details || t.remarks || t.title || '',
                            travel_details: t.travel_details || null,
                            proof_urls: t.bill_url ? [t.bill_url] : (t.proof_urls || []),
                            expense_type: 'non_apiary',
                            type: 'non_apiary',
                            tx_type: 'NON_APIARY_EXPENSE',
                            is_apiary: false,
                            apiary_id: null,
                            apiary_name: null,
                            approval_status: t.approval_status || 'PENDING',
                            approved_by: t.approved_by || null,
                            approved_by_name: t.approved_by_name || (typeof t.approved_by === 'object' ? (t.approved_by?.name || t.approved_by?.username) : null) || null,
                            approval_date: t.approval_date || null,
                            approval_comment: t.approval_comment || ''
                        });
                    }
                });
                return nonApiaryList;
            } catch (err) {
                console.warn('Error fetching non-apiary wallet expenses:', err);
                return [];
            }
        })();

        const [apiaryList, nonApiaryList] = await Promise.all([apiaryExpensesPromise, nonApiaryExpensesPromise]);
        
        console.log(`✅ Loaded ${apiaryList.length} Apiary expenses + ${nonApiaryList.length} Non-Apiary expenses`);
        return [...apiaryList, ...nonApiaryList];
    } catch (error) {
        console.error('Error fetching global expenses:', error);
        return [];
    }
};

/**
 * Uploads proofs (images/receipts) for a newly created expense.
 * @param {string} expenseId - The ID of the created expense.
 * @param {FormData} formData - The formData containing `photos`.
 * @returns {Promise<Object>} - The result of the upload.
 */
export const uploadExpenseProofs = async (expenseId, formData) => {
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/expenses/${expenseId}/proofs`;
        const response = await axios.post(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                source: "humblebee_admin",
                Authorization: "Bearer " + token
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error uploading expense proofs:", error.response || error);
        throw error;
    }
};

/**
 * Permanently deletes an expense record.
 * @param {string} expenseId - The ID of the expense to delete.
 * @returns {Promise<Object>} - The deletion result.
 */
export const deleteExpense = async (expenseId) => {
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/expenses/${expenseId}`;
        const response = await axios.delete(url, {
            headers: {
                'Content-Type': 'application/json',
                source: "humblebee_admin",
                Authorization: "Bearer " + token
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error deleting expense:", error.response || error);
        throw error;
    }
};

/**
 * Updates approval status (APPROVED / REJECTED / PENDING) for an expense directly in MongoDB.
 */
export const updateExpenseApprovalStatus = async (expenseId, approvalStatus, comment = '', approverName = null, isNonApiary = false) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://api.udyamita.org/field-view";
        
        const isPending = approvalStatus === 'PENDING';
        const finalApproverName = isPending ? "" : (approverName || 'Admin');
        
        const payload = {
            status: approvalStatus,
            approval_status: approvalStatus,
            approved_by: isPending ? null : finalApproverName,
            approved_by_name: isPending ? "" : finalApproverName,
            approval_date: isPending ? null : new Date().toISOString(),
            approval_comment: comment || "",
            comment: comment || ""
        };

        const headers = {
            'Content-Type': 'application/json',
            source: "humblebee_admin",
            Authorization: "Bearer " + token
        };

        // If non-apiary expense, hit the wallet non-apiary status endpoint
        if (isNonApiary) {
            try {
                const response = await axios.patch(`${fieldApi}/wallet/non-apiary-expense/${expenseId}/status`, payload, { headers });
                return response.data;
            } catch (err) {
                const response = await axios.patch(`${fieldApi}/nector/app/expenses/${expenseId}`, payload, { headers });
                return response.data;
            }
        } else {
            try {
                const response = await axios.patch(`${fieldApi}/nector/app/expenses/${expenseId}`, payload, { headers });
                return response.data;
            } catch (err) {
                const response = await axios.patch(`${fieldApi}/wallet/non-apiary-expense/${expenseId}/status`, payload, { headers });
                return response.data;
            }
        }
    } catch (error) {
        console.error("Error updating expense approval status:", error);
        throw error;
    }
};

/**
 * Fetches harvest logs for an apiary.
 * @param {string} apiaryId - The apiary_id to fetch harvests for.
 * @param {Object} filters - Optional filters (bee_species, harvest_method, date_from, etc.)
 * @returns {Promise<Array>} - List of harvest records.
 */
export const getHarvests = async (apiaryId, filters = {}) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        let url = `${fieldApi}/nector/app/harvests${apiaryId ? `?apiary_id=${apiaryId}` : '?'}`;

        if (filters.bee_species) url += `&bee_species=${filters.bee_species}`;
        if (filters.harvest_method) url += `&harvest_method=${filters.harvest_method}`;
        if (filters.colour) url += `&colour=${filters.colour}`;
        if (filters.date_from) url += `&date_from=${filters.date_from}`;
        if (filters.date_to) url += `&date_to=${filters.date_to}`;

        const response = await axios.get(url, {
            headers: {
                source: "humblebee_admin",
                Authorization: "Bearer " + token
            }
        });
        return response.data.harvests || [];
    } catch (error) {
        console.error('Error fetching harvest logs:', error);
        return [];
    }
}

/**
 * Updates a specific harvest record.
 * @param {string} harvestId - The ID of the harvest to update.
 * @param {Object} data - The updated data payload.
 * @returns {Promise<Object>} - The result of the update.
 */
export const updateHarvest = async (harvestId, data) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        const url = `${fieldApi}/nector/app/harvests/${harvestId}`;
        const response = await axios.patch(url, data, {
            headers: {
                source: "humblebee_admin",
                Authorization: "Bearer " + token
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error updating harvest:', error.response || error);
        throw error;
    }
}

/**
 * Permanently deletes a harvest record.
 * @param {string} harvestId - The ID of the harvest to delete.
 * @returns {Promise<Object>} - The deletion result.
 */
export const deleteHarvest = async (harvestId) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        const url = `${fieldApi}/nector/app/harvests/${harvestId}`;
        const response = await axios.delete(url, {
            headers: {
                source: "humblebee_admin",
                Authorization: "Bearer " + token
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error deleting harvest:', error.response || error);
        throw error;
    }
}

export const getMovements = async (apiaryId) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        const valueChainApi = process.env.REACT_APP_API_MENTOR_VAlUECHAIN_SERVICES || "https://devapi.udyamita.org/valuechain-services";
        
        let userId = null;
        try {
            if (token) {
                const decoded = jwtDecode(token);
                userId = decoded._id || decoded.id || decoded.userId || decoded.user_id;
            }
        } catch (e) {
            console.log('Token decode failed', e);
        }

        // Try the movements endpoint (verified from mobile app)
        const url = `${fieldApi}/nector/movements?apiary_id=${apiaryId}`;
        console.log('Fetching movements from:', url);
        
        try {
            const response = await axios.get(url, { 
                headers: { 
                    source: "humblebee_admin", 
                    Authorization: "Bearer " + token,
                    ...(userId && { userId })
                } 
            });
            return response.data.movements || response.data.activities || response.data || [];
        } catch (err) {
            console.warn('New movement endpoint failed, trying fallback /hive-activity-log');
            // Fallback to the older known endpoint
            const fallbackUrl = `${valueChainApi}/hive-activity-log/${apiaryId}`;
            const response = await axios.get(fallbackUrl, { 
                headers: { 
                    source: "HumblebeeWebApp", 
                    Authorization: "Bearer " + token,
                    ...(userId && { userId })
                } 
            });
            return response.data.activities || response.data.movements || response.data || [];
        }
    } catch (error) {
        console.error('Error fetching movements/activities:', error);
        return [];
    }
}

/**
 * Global Hive Statistics & Apiary List
 */
export const getGlobalApiaryStats = async (params = {}) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        
        const response = await axios.get(`${fieldApi}/nector/app/apiaries`, {
            params,
            headers: { 
                'Content-Type': 'application/json',
                source: "humblebee_admin", 
                Authorization: "Bearer " + token 
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching global apiary stats:', error);
        return { apiaries: [] };
    }
};

/**
 * Apiary Activity Summary (Metrics & Batches)
 */
export const getActivitySummary = async (apiaryId) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        
        const response = await axios.get(`${fieldApi}/nector/web/apiaries/${apiaryId}/activity-summary`, {
            headers: { 
                source: "humblebee_admin", 
                Authorization: "Bearer " + token 
            }
        });
        return response.data.data || response.data;
    } catch (error) {
        console.error('Error fetching activity summary:', error);
        return null;
    }
};

/**
 * Initial Deployments
 */
export const getInitialDeployments = async (apiaryId) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        
        // Exact path from mobile app research
        const url = `${fieldApi}/nector/app/deployments?apiary_id=${apiaryId}`;
        const response = await axios.get(url, {
            headers: { 
                source: "humblebee_admin", 
                Authorization: "Bearer " + token 
            }
        });
        return response.data.deployments || response.data.movements || response.data || [];
    } catch (error) {
        console.error('Error fetching deployments:', error);
        return [];
    }
};

/**
 * Hive Inventory & Batches
 */
export const getHiveBatches = async () => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        
        const response = await axios.get(`${fieldApi}/nector/web/hive-batches`, {
            headers: { 
                source: "humblebee_admin", 
                Authorization: "Bearer " + token 
            }
        });
        return response.data || [];
    } catch (error) {
        console.error('Error fetching hive batches:', error);
        return [];
    }
};

/**
 * Disable (soft-delete) an apiary. Sets is_active: false.
 * @param {string} apiaryId - The apiary_id to disable
 * @returns {Promise<Object>} - Updated apiary object
 */
export const disableApiary = async (apiaryId) => {
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/apiaries/${apiaryId}/disable`;
        const response = await axios.patch(url, {}, {
            headers: {
                'Content-Type': 'application/json',
                'source': 'humblebee_admin',
                'Authorization': `Bearer ${token}`,
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error disabling apiary:', error.response || error);
        throw error;
    }
};

/**
 * Enable (re-activate) a disabled apiary. Sets is_active: true.
 * @param {string} apiaryId - The apiary_id to enable
 * @returns {Promise<Object>} - Updated apiary object
 */
export const enableApiary = async (apiaryId) => {
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/apiaries/${apiaryId}/enable`;
        const response = await axios.patch(url, {}, {
            headers: {
                'Content-Type': 'application/json',
                'source': 'humblebee_admin',
                'Authorization': `Bearer ${token}`,
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error enabling apiary:', error.response || error);
        throw error;
    }
};

/**
 * Permanently delete an apiary. Blocked if linked records exist.
 * @param {string} apiaryId - The apiary_id to delete
 * @returns {Promise<Object>} - Deletion result
 */
export const deleteApiary = async (apiaryId) => {
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/apiaries/${apiaryId}`;
        const response = await axios.delete(url, {
            headers: {
                'Content-Type': 'application/json',
                'source': 'humblebee_admin',
                'Authorization': `Bearer ${token}`,
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error deleting apiary:', error.response || error);
        throw error;
    }
};

/**
 * Update an apiary. Only send fields that have changed.
 * @param {string} apiaryId - The apiary_id to update
 * @param {Object} data - Object with only the changed fields
 * @returns {Promise<Object>} - Update result
 */
export const updateApiary = async (apiaryId, data) => {
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/apiaries/${apiaryId}`;
        const response = await axios.patch(url, data, {
            headers: {
                'Content-Type': 'application/json',
                'source': 'humblebee_admin',
                'Authorization': `Bearer ${token}`,
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error updating apiary:', error.response || error);
        throw error;
    }
};

/**
 * Permanently delete a hive batch.
 * @param {string} batchId - The batch_id to delete
 * @returns {Promise<Object>} - Deletion result
 */
export const deleteHiveBatch = async (batchId) => {
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/web/hive-batches/${batchId}`;
        const response = await axios.delete(url, {
            headers: {
                'Content-Type': 'application/json',
                'source': 'humblebee_admin',
                'Authorization': `Bearer ${token}`,
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error deleting hive batch:', error.response || error);
        throw error;
    }
};

/**
 * Soft-delete (archive) a hive batch.
 * @param {string} batchId - The batch_id to disable
 * @returns {Promise<Object>} - Result
 */
export const disableHiveBatch = async (batchId) => {
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/web/hive-batches/${batchId}/disable`;
        const response = await axios.patch(url, {}, {
            headers: {
                'Content-Type': 'application/json',
                'source': 'humblebee_admin',
                'Authorization': `Bearer ${token}`,
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error disabling hive batch:', error.response || error);
        throw error;
    }
};

/**
 * Universal Revoke: Remove hives of a batch from an apiary without needing a deployment log.
 * @param {string} batchId - The _id or batch_id of the batch
 * @param {string} apiaryId - The apiary_id to revoke from
 * @returns {Promise<Object>} - Result
 */
export const revokeHiveBatchLocation = async (batchId, apiaryId) => {
    try {
        const token = cookies.get('access_token');
        const url = `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/web/hive-batches/${batchId}/revoke-location`;
        const response = await axios.patch(url, { apiary_id: apiaryId }, {
            headers: {
                'Content-Type': 'application/json',
                'source': 'humblebee_admin',
                'Authorization': `Bearer ${token}`,
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error revoking hive batch location:', error.response || error);
        throw error;
    }
};

/**
 * Fetches colony logs (adjustment history) for an apiary.
 * @param {string} apiaryId - The apiary_id to fetch logs for.
 * @returns {Promise<Object>} - Object containing count and logs array.
 */
export const getColonyLogs = async (apiaryId) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        const url = `${fieldApi}/nector/app/apiaries/${apiaryId}/colony-logs`;

        const response = await axios.get(url, {
            headers: {
                source: "humblebee_admin",
                Authorization: "Bearer " + token
            }
        });
        return response.data || { count: 0, logs: [] };
    } catch (error) {
        console.error('Error fetching colony logs:', error);
        return { count: 0, logs: [] };
    }
};

/**
 * Creates a new colony log (hive adjustment).
 * @param {string} apiaryId - The apiary_id.
 * @param {Object} data - Payload: { type, source_or_reason, date, count }.
 * @returns {Promise<Object>} - Result of the creation.
 */
export const createColonyLog = async (apiaryId, data) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        const url = `${fieldApi}/nector/app/apiaries/${apiaryId}/colony-logs`;

        const response = await axios.post(url, data, {
            headers: {
                'Content-Type': 'application/json',
                source: "humblebee_admin",
                Authorization: "Bearer " + token
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error creating colony log:', error.response || error);
        throw error;
    }
};
/**
 * Fetches aggregated hive movement data for network flow visualization.
 * @param {Object} params - Query filters (startDate, endDate, batch_id).
 * @returns {Promise<Object>} - Nodes and Links data structure.
 */
export const getHiveMovementFlow = async (params = {}) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        const url = `${fieldApi}/nector/web/analytics/hive-movement-flow`;

        const response = await axios.get(url, {
            params,
            headers: {
                'Content-Type': 'application/json',
                source: "humblebee_admin",
                Authorization: "Bearer " + token
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching hive movement flow:', error);
        return { success: false, nodes: [], links: [] };
    }
};

/**
 * Fetches all hive activity/visit logs of a specific apiary.
 * @param {string} apiaryId - The apiary ID.
 * @returns {Promise<Array>} - List of activity logs.
 */
export const getHiveActivityLogs = async (apiaryId) => {
    try {
        const token = cookies.get('access_token');
        const valueChainApi = process.env.REACT_APP_API_MENTOR_VAlUECHAIN_SERVICES || "https://devapi.udyamita.org/valuechain-services";
        const url = `${valueChainApi}/hive-activity-log/${apiaryId || 'all'}`;

        console.log(`[getHiveActivityLogs] Fetching URL: ${url}`);

        const response = await axios.get(url, {
            headers: {
                source: "HumblebeeWebApp",
                Authorization: "Bearer " + token
            }
        });
        
        if (response.data?.error) {
            console.warn(`[getHiveActivityLogs] Backend returned error for ${apiaryId || 'all'}:`, JSON.stringify(response.data, null, 2));
        } else {
            console.log(`[getHiveActivityLogs] Response for ${apiaryId || 'all'}:`, response.data);
        }
        return response.data.activities || [];
    } catch (error) {
        console.error(`[getHiveActivityLogs] Network error fetching for ${apiaryId || 'all'}:`, error.message);
        return [];
    }
};

/**
 * Fetches Beekeeping apiary details by ID.
 * @param {string} apiaryId - The apiary ID.
 * @returns {Promise<Object|null>} - The apiary details or null.
 */
export const getBeekeepingApiaryDetails = async (apiaryId) => {
    try {
        const token = cookies.get('access_token');
        const valueChainApi = process.env.REACT_APP_API_MENTOR_VAlUECHAIN_SERVICES || "https://devapi.udyamita.org/valuechain-services";
        const url = `${valueChainApi}/beekeeping/apiaries/${apiaryId}`;

        const response = await axios.get(url, {
            headers: {
                source: "HumblebeeWebApp",
                Authorization: "Bearer " + token
            }
        });
        return response.data?.apiaryDetails || null;
    } catch (error) {
        console.error(`[getBeekeepingApiaryDetails] Error fetching apiary details for ${apiaryId}:`, error.message);
        return null;
    }
};

/**
 * Fetches hive activity logs across all active apiaries.
 * Queries the `/all` endpoint and maps apiary names client-side.
 */
export const getAllHiveActivityLogs = async () => {
    try {
        console.log("[getAllHiveActivityLogs] Initiating global fetch...");
        const apiaries = await getAllApiariesService();
        console.log(`[getAllHiveActivityLogs] Fetched ${apiaries?.length || 0} apiaries`);

        const token = cookies.get('access_token');
        const valueChainApi = process.env.REACT_APP_API_MENTOR_VAlUECHAIN_SERVICES || "https://devapi.udyamita.org/valuechain-services";
        const url = `${valueChainApi}/hive-activity-log/all`;

        console.log(`[getAllHiveActivityLogs] Fetching all logs from: ${url}`);

        const response = await axios.get(url, {
            headers: {
                source: "HumblebeeWebApp",
                Authorization: "Bearer " + token
            }
        });

        if (response.data?.error) {
            console.error("[getAllHiveActivityLogs] Backend returned error:", JSON.stringify(response.data, null, 2));
            
            // FALLBACK: If /all endpoint fails, do parallel queries as fallback
            console.warn("[getAllHiveActivityLogs] Falling back to parallel fetching...");
            return await getParallelHiveActivityLogs(apiaries);
        }

        const logs = response.data.activities || [];
        console.log(`[getAllHiveActivityLogs] Fetched ${logs.length} raw activity logs`);

        const mappedLogs = mapLogsWithApiaryNames(logs, apiaries);

        // Fetch names dynamically for any logs where apiaryName is still 'N/A' (e.g. they reference mobile app's 'apiaries' collection)
        const naLogs = mappedLogs.filter(log => log.apiaryName === 'N/A' && log.apiary_id);
        if (naLogs.length > 0) {
            const uniqueNaApiaryIds = [...new Set(naLogs.map(log => log.apiary_id.toString()))];
            console.log(`[getAllHiveActivityLogs] Resolving names for ${uniqueNaApiaryIds.length} Beekeeping apiaries...`);
            
            const apiaryDetailsPromises = uniqueNaApiaryIds.map(async (id) => {
                const details = await getBeekeepingApiaryDetails(id);
                return { id, name: details?.name || 'N/A' };
            });
            const resolvedApiaries = await Promise.all(apiaryDetailsPromises);
            
            const resolvedMap = {};
            resolvedApiaries.forEach(item => {
                resolvedMap[item.id] = item.name;
            });

            mappedLogs.forEach(log => {
                if (log.apiaryName === 'N/A' && log.apiary_id) {
                    log.apiaryName = resolvedMap[log.apiary_id.toString()] || 'N/A';
                }
            });
        }

        return mappedLogs;
    } catch (error) {
        console.error("[getAllHiveActivityLogs] Network/Server Error:", error.message);
        return [];
    }
};

/**
 * Helper to fetch logs in parallel across all apiaries.
 */
const getParallelHiveActivityLogs = async (apiaries) => {
    const logsPromises = apiaries.map(async (apiary) => {
        const dbId = apiary._id?.toString() || apiary._id;
        const customApiaryId = apiary.apiary_id;
        if (!dbId) return [];

        try {
            const logs = await getHiveActivityLogs(dbId);
            return (logs || []).map(log => ({
                ...log,
                apiaryId: customApiaryId || dbId,
                apiaryName: apiary.apiary_name || 'N/A'
            }));
        } catch (err) {
            console.error(`[getParallelHiveActivityLogs] Error fetching logs for apiary ${dbId}:`, err);
            return [];
        }
    });

    const results = await Promise.all(logsPromises);
    return results.flat().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
};

/**
 * Helper to map apiary names to logs.
 */
const mapLogsWithApiaryNames = (logs, apiaries) => {
    const apiaryMap = {};
    if (Array.isArray(apiaries)) {
        apiaries.forEach(apiary => {
            const id = apiary._id || apiary.apiary_id;
            if (id) {
                apiaryMap[id.toString()] = apiary.apiary_name || 'N/A';
            }
        });
    }

    return logs.map(log => {
        const apiaryIdStr = log.apiary_id ? log.apiary_id.toString() : '';
        return {
            ...log,
            apiaryId: log.apiary_id,
            apiaryName: apiaryMap[apiaryIdStr] || 'N/A'
        };
    });
};

/**
 * Deletes a hive activity log.
 * @param {string} logId - The ID of the activity log to delete.
 */
export const deleteHiveActivityLog = async (logId) => {
    try {
        const token = cookies.get('access_token');
        const valueChainApi = process.env.REACT_APP_API_MENTOR_VAlUECHAIN_SERVICES || "https://devapi.udyamita.org/valuechain-services";
        const url = `${valueChainApi}/hive-activity-log/activity-update/${logId}`;

        const response = await axios.delete(url, {
            headers: {
                source: "HumblebeeWebApp",
                Authorization: "Bearer " + token
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error deleting hive activity log:", error);
        throw error;
    }
};

/**
 * Fetches all beekeeping movements/migrations globally.
 */
export const getAllMovements = async () => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        
        let userId = null;
        try {
            if (token) {
                const decoded = jwtDecode(token);
                userId = decoded._id || decoded.id || decoded.userId || decoded.user_id;
            }
        } catch (e) {
            console.log('Token decode failed', e);
        }

        const url = `${fieldApi}/nector/movements`;
        const response = await axios.get(url, { 
            headers: { 
                source: "humblebee_admin", 
                Authorization: "Bearer " + token,
                ...(userId && { userId })
            } 
        });
        return response.data.movements || response.data || [];
    } catch (error) {
        console.error('Error fetching all movements:', error);
        return [];
    }
};

/**
 * Fetches expenses associated with a specific hive movement ID.
 */
export const getMovementExpenses = async (movementId) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        const url = `${fieldApi}/nector/app/expenses?movement_id=${movementId}&include_settlement=true`;

        const response = await axios.get(url, {
            headers: { source: "humblebee_admin", Authorization: "Bearer " + token }
        });
        return response.data.expenses || response.data || [];
    } catch (error) {
        console.error('Error fetching movement expenses:', error);
        return [];
    }
};



/**
 * Updates migration record lead and team.
 */
export const updateMovement = async (id, data) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        const url = `${fieldApi}/nector/app/movements/${id}`;

        const response = await axios.put(url, data, {
            headers: { source: "humblebee_admin", Authorization: "Bearer " + token }
        });
        return response.data;
    } catch (error) {
        console.error('Error updating movement record:', error);
        throw error;
    }
};

/**
 * Soft deletes migration record.
 */
export const deleteMovementRecord = async (id) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        const url = `${fieldApi}/nector/app/movements/${id}`;

        const response = await axios.delete(url, {
            headers: { source: "humblebee_admin", Authorization: "Bearer " + token }
        });
        return response.data;
    } catch (error) {
        console.error('Error deleting movement record:', error);
        throw error;
    }
};

/* -------------------------------------------------------------
 * 💰 ADVANCES & EXPENSES MODULE API SERVICES
 * ------------------------------------------------------------- */

/**
 * 1. Overview Dashboard Stats
 */
export const getAdvancesOverviewService = async () => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        const url = `${fieldApi}/nector/app/advances/overview`;

        const response = await axios.get(url, {
            headers: { source: "humblebee_admin", Authorization: "Bearer " + token }
        });
        return response.data.data || response.data || null;
    } catch (error) {
        console.warn('Overview API not ready, fallback to client-side compute:', error);
        return null;
    }
};

/**
 * 2. Pending Expenses for Review
 */
export const getPendingExpensesService = async () => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        const url = `${fieldApi}/nector/app/expenses?approval_status=PENDING`;

        const response = await axios.get(url, {
            headers: { source: "humblebee_admin", Authorization: "Bearer " + token }
        });
        return response.data.expenses || response.data || [];
    } catch (error) {
        console.error('Error fetching pending expenses:', error);
        return [];
    }
};

/**
 * 3. Approve / Reject Single Expense
 */
export const updateExpenseApprovalService = async (expenseId, decision, reason = '') => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        const url = `${fieldApi}/nector/app/expenses/${expenseId}`;

        const response = await axios.patch(url, {
            approval_status: decision.toUpperCase(),
            approval_comment: reason || null,
            approval_date: new Date()
        }, {
            headers: { source: "humblebee_admin", Authorization: "Bearer " + token }
        });
        return response.data;
    } catch (error) {
        console.error('Error updating expense status:', error);
        throw error;
    }
};

/**
 * 4. Bulk Approve Expenses
 */
export const bulkApproveExpensesService = async (expenseIds) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        const url = `${fieldApi}/nector/app/expenses/bulk-approve`;

        const response = await axios.post(url, { expense_ids: expenseIds }, {
            headers: { source: "humblebee_admin", Authorization: "Bearer " + token }
        });
        return response.data;
    } catch (error) {
        console.warn('Bulk approve endpoint fallback, approving individually:', error);
        // Fallback: individual patch
        await Promise.all(
            expenseIds.map(id => updateExpenseApprovalService(id, 'APPROVED'))
        );
        return { success: true };
    }
};

/**
 * 5. Apiary P&L Cost Breakdown
 */
export const getApiaryPnLService = async (apiaryId) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        const url = `${fieldApi}/nector/app/analytics/apiary-pnl?apiary_id=${apiaryId}`;

        const response = await axios.get(url, {
            headers: { source: "humblebee_admin", Authorization: "Bearer " + token }
        });
        return response.data.data || response.data;
    } catch (error) {
        return null;
    }
};

/**
 * 6. Project P&L Cost Breakdown
 */
export const getProjectPnLService = async (projectId) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        const url = `${fieldApi}/nector/app/analytics/project-pnl?project_id=${projectId}`;

        const response = await axios.get(url, {
            headers: { source: "humblebee_admin", Authorization: "Bearer " + token }
        });
        return response.data.data || response.data;
    } catch (error) {
        return null;
    }
};

/**
 * 7. Unpaid Reimbursements for Payroll
 */
export const getUnpaidPayrollService = async () => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        const url = `${fieldApi}/nector/app/payroll/unpaid`;

        const response = await axios.get(url, {
            headers: { source: "humblebee_admin", Authorization: "Bearer " + token }
        });
        return response.data.data || response.data;
    } catch (error) {
        return null;
    }
};

/**
 * 8. Create Payout Batch
 */
export const createPayrollBatchService = async (batchData) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        const url = `${fieldApi}/nector/app/payroll/create-batch`;

        const response = await axios.post(url, batchData, {
            headers: { source: "humblebee_admin", Authorization: "Bearer " + token }
        });
        return response.data;
    } catch (error) {
        console.error('Error creating payroll batch:', error);
        throw error;
    }
};

/**
 * 9. Member Passbook Statement
 */
export const getMemberPassbookService = async (memberId) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        const url = `${fieldApi}/nector/app/advances/passbook/${memberId}`;

        const response = await axios.get(url, {
            headers: { source: "humblebee_admin", Authorization: "Bearer " + token }
        });
        return response.data.data || response.data;
    } catch (error) {
        return null;
    }
};

/**
 * 10. Settle / Reset Advance Balance
 */
export const settleAdvanceService = async (settleData) => {
    try {
        const token = cookies.get('access_token');
        const fieldApi = process.env.REACT_APP_API_APP_FIELD_URL || "https://devapi.udyamita.org/field-view";
        const url = `${fieldApi}/nector/app/advances/settle`;

        const response = await axios.post(url, settleData, {
            headers: { source: "humblebee_admin", Authorization: "Bearer " + token }
        });
        return response.data;
    } catch (error) {
        console.error('Error settling advance:', error);
        throw error;
    }
};


/* ------------------------------------------------------------------
 * ADVANCE REQUESTS (admin)
 * ------------------------------------------------------------------ */
const advanceRequestHeaders = () => ({
    'Content-Type': 'application/json',
    source: 'humblebee_admin',
    Authorization: `Bearer ${cookies.get('access_token')}`,
});

export const getAdvanceReasons = async () => {
    try {
        const response = await axios.get(
            `${process.env.REACT_APP_API_APP_FIELD_URL}/wallet/advance-reasons`,
            { headers: advanceRequestHeaders() }
        );
        return response.data?.reasons || [];
    } catch (error) {
        console.error('Error fetching advance reasons:', error);
        return [];
    }
};

/**
 * Paged list with filters: { status, reason, from, to, search, page, limit }.
 */
export const getAdvanceRequests = async (filters = {}) => {
    const params = {};
    Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') params[key] = value;
    });
    const response = await axios.get(
        `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/advance-requests`,
        { headers: advanceRequestHeaders(), params }
    );
    return response.data;
};

const decideAdvanceRequest = async (id, status, comment) => {
    const response = await axios.patch(
        `${process.env.REACT_APP_API_APP_FIELD_URL}/nector/app/advance-requests/${id}/status`,
        { status, comment: comment || '' },
        { headers: advanceRequestHeaders() }
    );
    return response.data;
};

export const approveAdvanceRequest = (id, comment) => decideAdvanceRequest(id, 'APPROVED', comment);
export const rejectAdvanceRequest = (id, comment) => decideAdvanceRequest(id, 'REJECTED', comment);
